import React, { useEffect, useState, useRef } from 'react';
import { Spinner } from 'reactstrap';
import axios from 'axios';
import { GET_REKAP_DTSEN_KECAMATAN, API_TIMEOUT } from '../../utils';
import { numberWithCommas } from '../../utils/func';
import s from './RekapanDtsenKecamatan.module.scss';

const CARD_WIDTH = 280;
const GAP = 16;
const AUTO_SPEED = 0.6;

const toSmallCaps = (t) =>
  (t || '')
    .toString()
    .toLowerCase()
    .replace(/(?:^|\s)\S/g, (c) => c.toUpperCase());

const KecamatanCard = ({ item, idx }) => {
  const total = Number(item.jumlahOrang) || 0;
  const desil12 = (Number(item.Desil_1) || 0) + (Number(item.Desil_2) || 0);
  const pctDesil12 = total > 0 ? ((desil12 / total) * 100).toFixed(1) : '0';
  return (
    <div className={s.sliderCard} key={`${item.kecamatan}-${idx}`}>
      <div className={s.card}>
        <div className={s.cardHeader}>
          <span className={s.cardBadge}>Kecamatan</span>
          <h6 className={s.cardTitle}>{toSmallCaps(item.kecamatan || '-')}</h6>
        </div>
        <div className={s.cardBody}>
          <div className={s.statMain}>
            <span className={s.statLabel}>Total Penduduk</span>
            <span className={s.statValue}>{numberWithCommas(total)}</span>
          </div>
          <div className={s.desilGrid}>
            <div className={s.desilItem}>
              <span className={s.desilLabel}>Desil 1</span>
              <span className={s.desilValue}>{numberWithCommas(item.Desil_1 || 0)}</span>
            </div>
            <div className={s.desilItem}>
              <span className={s.desilLabel}>Desil 2</span>
              <span className={s.desilValue}>{numberWithCommas(item.Desil_2 || 0)}</span>
            </div>
            <div className={s.desilItem}>
              <span className={s.desilLabel}>Desil 3</span>
              <span className={s.desilValue}>{numberWithCommas(item.Desil_3 || 0)}</span>
            </div>
            <div className={s.desilItem}>
              <span className={s.desilLabel}>Desil 4</span>
              <span className={s.desilValue}>{numberWithCommas(item.Desil_4 || 0)}</span>
            </div>
            <div className={s.desilItem}>
              <span className={s.desilLabel}>Desil 5</span>
              <span className={s.desilValue}>{numberWithCommas(item.Desil_5 || 0)}</span>
            </div>
            <div className={s.desilItem}>
              <span className={s.desilLabel}>Desil 6–10</span>
              <span className={s.desilValue}>{numberWithCommas(item.Desil_6_10 || 0)}</span>
            </div>
          </div>
          <div className={s.cardFooter}>
            <span className={s.footerLabel}>Desil 1+2</span>
            <span className={s.footerValue}>{numberWithCommas(desil12)} ({pctDesil12}%)</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const RekapanDtsenKecamatan = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scrollOffset, setScrollOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef({ x: 0, offset: 0 });

  const oneSetWidth = data.length * (CARD_WIDTH + GAP);

  useEffect(() => {
    axios
      .get(GET_REKAP_DTSEN_KECAMATAN, { timeout: API_TIMEOUT })
      .then((res) => {
        const raw = res.data?.data || [];
        setData(Array.isArray(raw) ? raw : []);
      })
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!data.length || isDragging) return;
    let id;
    const tick = () => {
      setScrollOffset((prev) => {
        let next = prev + AUTO_SPEED;
        if (next >= oneSetWidth) next -= oneSetWidth;
        return next;
      });
      id = requestAnimationFrame(tick);
    };
    id = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(id);
  }, [data.length, oneSetWidth, isDragging]);

  const getClientX = (e) => (e.touches ? e.touches[0].clientX : e.clientX);

  const handleDragStart = (e) => {
    setIsDragging(true);
    dragStart.current = { x: getClientX(e), offset: scrollOffset };
  };

  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e) => {
      if (e.cancelable) e.preventDefault();
      const dx = dragStart.current.x - (e.touches ? e.touches[0].clientX : e.clientX);
      let next = dragStart.current.offset + dx;
      if (next < 0) next = 0;
      if (next > oneSetWidth) next = oneSetWidth;
      setScrollOffset(next);
    };
    const onEnd = () => setIsDragging(false);
    document.addEventListener('mousemove', onMove, { passive: false });
    document.addEventListener('mouseup', onEnd);
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onEnd);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onEnd);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
    };
  }, [isDragging, oneSetWidth]);

  if (loading) {
    return (
      <div className={s.loadingWrap}>
        <Spinner color="primary" />
        <p className={s.loadingText}>Memuat data DTSEN per kecamatan...</p>
      </div>
    );
  }

  if (!data.length) {
    return (
      <div className={s.emptyWrap}>
        <i className="eva eva-map-outline" />
        <p>Belum ada data DTSEN per kecamatan</p>
      </div>
    );
  }

  return (
    <div className={s.rekapanWrap}>
      <h6 className={s.sectionTitle}>Rekapan DTSEN Per Kecamatan</h6>
      <p className={s.sectionDesc}>Data penduduk menurut desil kesejahteraan per kecamatan di Kabupaten Rote Ndao.</p>
      <div
        className={s.sliderWrap}
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
      >
        <div className={s.sliderTrack} style={{ transform: `translateX(-${scrollOffset}px)` }}>
          {data.map((item, idx) => (
            <KecamatanCard key={`a-${item.kecamatan}-${idx}`} item={item} idx={idx} />
          ))}
          {data.map((item, idx) => (
            <KecamatanCard key={`b-${item.kecamatan}-${idx}`} item={item} idx={idx} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default RekapanDtsenKecamatan;
